import { Controller } from 'react-hook-form';
import { Alert, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../shared/components/FormDialog';
import { MoneyInputField, canonicalToBrl } from '../../shared/components/MoneyInput';
import { ClienteSelect } from '../../shared/components/ClienteSelect';
import { useCrudFormDialog } from '../../shared/api/useCrudFormDialog';
import { purchaseRequestsHooks } from './hooks';
import { purchaseRequestInputSchema, type PurchaseRequestFormValues } from './schemas';
import type { PurchaseRequestDto, PurchaseRequestInput } from './types';

export interface PurchaseRequestFormDialogProps {
  open: boolean;
  purchaseRequest: PurchaseRequestDto | null;
  onClose: () => void;
}

const DEFAULT_VALUES: PurchaseRequestFormValues = {
  referencia: '',
  descricaoNotaFiscal: null,
  valorInicial: '',
  valorComplemento: '0',
  clienteId: null,
};

export function PurchaseRequestFormDialog({
  open,
  purchaseRequest,
  onClose,
}: PurchaseRequestFormDialogProps): JSX.Element {
  const { t } = useTranslation();

  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    PurchaseRequestFormValues,
    PurchaseRequestDto,
    PurchaseRequestInput,
    number
  >({
    open,
    entity: purchaseRequest,
    schema: purchaseRequestInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (rc) => ({
      referencia: rc.referencia,
      descricaoNotaFiscal: rc.descricaoNotaFiscal,
      // Valores monetários chegam como `number` (o DTO já reflete isso — o
      // adapter do BFF converte o decimal GeneXus). O schema do form espera
      // string (o KB aceita string no body), então coagimos aqui. NÃO remover
      // como "redundante": sem isso o Zod acusa "Expected string, received number".
      valorInicial: String(rc.valorInicial),
      valorComplemento: String(rc.valorComplemento),
      clienteId: rc.clienteId,
    }),
    getId: (rc) => rc.id,
    hooks: {
      useCreate: purchaseRequestsHooks.useCreate,
      useUpdate: purchaseRequestsHooks.useUpdate,
    },
    messages: {
      created: t('features.requisicoesCompra.toasts.created'),
      updated: t('features.requisicoesCompra.toasts.updated'),
    },
    focusField: 'referencia',
    onClose,
  });

  return (
    <FormDialog
      open={open}
      title={
        isEdit
          ? t('features.requisicoesCompra.editTitle')
          : t('features.requisicoesCompra.createTitle')
      }
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
      maxWidth="md"
    >
      <Stack spacing={2}>
        <TextField
          label={t('features.requisicoesCompra.fields.referencia')}
          fullWidth
          inputProps={{ maxLength: 60 }}
          error={Boolean(form.formState.errors.referencia)}
          helperText={form.formState.errors.referencia?.message}
          {...form.register('referencia')}
        />

        <Controller
          control={form.control}
          name="descricaoNotaFiscal"
          render={({ field, fieldState }) => (
            <TextField
              label={t('features.requisicoesCompra.fields.descricaoNotaFiscal')}
              helperText={fieldState.error?.message}
              error={Boolean(fieldState.error)}
              multiline
              minRows={2}
              inputProps={{ maxLength: 500 }}
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
              onBlur={field.onBlur}
              inputRef={field.ref}
            />
          )}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <MoneyInputField
            control={form.control}
            name="valorInicial"
            label={t('features.requisicoesCompra.fields.valorInicial')}
            fullWidth
            disabled={isEdit}
            helperText={
              isEdit
                ? t('features.requisicoesCompra.fields.valorInicialReadonlyHelper')
                : t('features.requisicoesCompra.fields.valorInicialHelper')
            }
            inputProps={{ maxLength: 18 }}
          />
          <MoneyInputField
            control={form.control}
            name="valorComplemento"
            label={t('features.requisicoesCompra.fields.valorComplemento')}
            fullWidth
            helperText={t('features.requisicoesCompra.fields.valorComplementoHelper')}
            inputProps={{ maxLength: 18 }}
          />
        </Stack>

        <Controller
          control={form.control}
          name="clienteId"
          render={({ field, fieldState }) => (
            <ClienteSelect
              value={field.value ?? null}
              onChange={(id) => field.onChange(id)}
              label={t('features.requisicoesCompra.fields.cliente')}
              error={Boolean(fieldState.error)}
              helperText={
                fieldState.error?.message ?? t('features.requisicoesCompra.fields.clienteHelper')
              }
            />
          )}
        />

        {isEdit && purchaseRequest && (
          <Alert severity="info">
            {t('features.requisicoesCompra.summary', {
              saldo: `R$ ${canonicalToBrl(purchaseRequest.saldo) || purchaseRequest.saldo}`,
              status: t(`features.requisicoesCompra.statusLabel.${purchaseRequest.status}`, {
                defaultValue: purchaseRequest.status,
              }),
            })}
          </Alert>
        )}
      </Stack>
    </FormDialog>
  );
}
